import classNames from 'classnames'
import { FunctionComponent, ReactNode, useId, useState } from 'react'
import { FOCUS_VISIBLE_RING } from '../../../lib/a11yFocus'

export type UniversalPageLayoutCollapsible = {
	/** Header text shown next to the toggle, e.g. 'Filters and search'. */
	title: string
	content: ReactNode
	/** false renders the content with no header/toggle at all. Default true. */
	collapsible?: boolean
	/** Uncontrolled initial state. Default true (open). Ignored when `open` is supplied. */
	defaultOpen?: boolean
	/** Controlled state. When supplied, the component does not own the state. */
	open?: boolean
	onOpenChange?: (open: boolean) => void
	/** Heading level for the zone title. Default 'h2'. */
	headingLevel?: 'h2' | 'h3' | 'h4'
}

type CollapsibleZoneProps = UniversalPageLayoutCollapsible & {
	testIdPrefix: string
	wrapperClass?: string
}

const CollapsibleZone: FunctionComponent<CollapsibleZoneProps> = ({
	title,
	content,
	collapsible = true,
	defaultOpen = true,
	open,
	onOpenChange,
	headingLevel: Heading = 'h2',
	testIdPrefix,
	wrapperClass,
}) => {
	const contentId = useId()
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
	const isControlled = open !== undefined
	const isOpen = isControlled ? open : uncontrolledOpen

	const handleToggle = () => {
		const next = !isOpen
		if (!isControlled) {
			setUncontrolledOpen(next)
		}
		onOpenChange?.(next)
	}

	if (!collapsible) {
		return (
			<div className={wrapperClass} data-testid={testIdPrefix}>
				<Heading className='text-sm text-charcoal-100'>{title}</Heading>
				<div data-testid={`${testIdPrefix}-content`}>{content}</div>
			</div>
		)
	}

	return (
		<div className={wrapperClass} data-testid={testIdPrefix}>
			<div className='flex items-center justify-between'>
				<Heading className='text-sm text-charcoal-100'>{title}</Heading>
				<button
					type='button'
					aria-expanded={isOpen}
					aria-controls={contentId}
					aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${title}`}
					data-testid={`${testIdPrefix}-toggle`}
					className={classNames('p-1', FOCUS_VISIBLE_RING)}
					onClick={handleToggle}
				>
					<i className={classNames('fa-solid', isOpen ? 'fa-minus' : 'fa-plus', 'text-algae-300')} aria-hidden='true' />
				</button>
			</div>
			<div
				id={contentId}
				data-testid={`${testIdPrefix}-content`}
				hidden={!isOpen}
				className={classNames(!isOpen && 'hidden')}
			>
				{content}
			</div>
		</div>
	)
}

export default CollapsibleZone
